import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface FileUploadResult {
  files: File[];
  isValid: boolean;
  errors: string[];
}

export interface FileValidationRules {
  maxFiles: number;
  maxFileSize: number;
  maxPagesPerFile: number;
  allowedTypes: string[];
  allowedExtensions: string[];
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent {
  @Input() isVisible: boolean = false;
  @Output() filesSelected = new EventEmitter<FileUploadResult>();
  @Output() close = new EventEmitter<void>();

  selectedFiles: File[] = [];
  dragOver: boolean = false;
  errors: string[] = [];
  
  // File validation rules
  private readonly validationRules: FileValidationRules = {
    maxFiles: 3,
    maxFileSize: 3 * 1024 * 1024, // 3MB for PDF/DOCX
    maxPagesPerFile: 30,
    allowedTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ],
    allowedExtensions: ['.pdf', '.docx', '.txt']
  };
  
  // File size limits by type
  private readonly FILE_SIZE_LIMITS = {
    'application/pdf': 3 * 1024 * 1024, // 3MB
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 3 * 1024 * 1024, // 3MB
    'text/plain': 1 * 1024 * 1024 // 1MB for TXT files
  };

  constructor() {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    
    if (event.dataTransfer?.files) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  private handleFiles(files: File[]): void {
    this.errors = [];
    this.selectedFiles = [];
    
    // Check total file count limit
    if (files.length > this.validationRules.maxFiles) {
      this.errors.push(`Maximum ${this.validationRules.maxFiles} files allowed. You selected ${files.length} files.`);
      this.filesSelected.emit({
        files: [],
        isValid: false,
        errors: this.errors
      });
      return;
    }
    
    for (const file of files) {
      const validation = this.validateFile(file);
      if (validation.isValid) {
        this.selectedFiles.push(file);
      } else {
        this.errors.push(...validation.errors);
      }
    }

    this.filesSelected.emit({
      files: this.selectedFiles,
      isValid: this.errors.length === 0,
      errors: this.errors
    });
  }

  private validateFile(file: File): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check file type by MIME type
    if (!this.validationRules.allowedTypes.includes(file.type)) {
      // Fallback to extension check for cases where MIME type might not be detected
      const fileExtension = this.getFileExtension(file.name);
      if (!this.validationRules.allowedExtensions.includes(fileExtension)) {
        errors.push(`${file.name}: Only PDF, DOCX, and TXT files are allowed`);
        return { isValid: false, errors };
      }
    }

    // Check file size based on file type
    const maxSizeForType = this.FILE_SIZE_LIMITS[file.type as keyof typeof this.FILE_SIZE_LIMITS];
    if (maxSizeForType && file.size > maxSizeForType) {
      const sizeLimitMB = maxSizeForType / (1024 * 1024);
      errors.push(`${file.name}: File size exceeds ${sizeLimitMB}MB limit for ${this.getFileTypeDisplayName(file.type)} files`);
    }

    // Additional validation for specific file types
    if (file.type === 'text/plain' && file.size > this.FILE_SIZE_LIMITS['text/plain']) {
      errors.push(`${file.name}: TXT files are limited to 1MB maximum`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private getFileExtension(filename: string): string {
    return filename.toLowerCase().substring(filename.lastIndexOf('.'));
  }

  private getFileTypeDisplayName(mimeType: string): string {
    switch (mimeType) {
      case 'application/pdf':
        return 'PDF';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'DOCX';
      case 'text/plain':
        return 'TXT';
      default:
        return 'Unknown';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onClose(): void {
    this.close.emit();
  }

  onUpload(): void {
    if (this.selectedFiles.length > 0) {
      this.filesSelected.emit({
        files: this.selectedFiles,
        isValid: true,
        errors: []
      });
    }
  }

  triggerFileInput(): void {
    const input = document.getElementById('file-input') as HTMLInputElement;
    if (input) {
      input.click();
    }
  }
}
